import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, errorMessage } from '../api.js'
import { useToast } from '../../components/ui/Toast.jsx'
import { qk } from './keys.js'

export const supportTicketsQuery = queryOptions({
  queryKey: qk.supportTickets,
  queryFn: ({ signal }) => api('/support/tickets', { signal }).then((d) => d.tickets),
  staleTime: 30_000,
})

export function useSupportTickets() {
  return useQuery(supportTicketsQuery)
}

/** Model calls store nothing; they only return text for the page to hold. */
export function useAnalyzeTicket() {
  const toast = useToast()
  return useMutation({
    mutationFn: (ticket) => api('/support/analyze', { method: 'POST', body: { ticket } }),
    onError: (error) => toast.error(errorMessage(error, 'Could not analyze the ticket.')),
  })
}

export function useDraftReply() {
  const toast = useToast()
  return useMutation({
    mutationFn: (body) => api('/support/draft', { method: 'POST', body }),
    onError: (error) => toast.error(errorMessage(error, 'Could not draft a reply.')),
  })
}

export function useImproveReply() {
  const toast = useToast()
  return useMutation({
    mutationFn: (reply) => api('/support/improve', { method: 'POST', body: { reply } }).then((d) => d.reply),
    onError: (error) => toast.error(errorMessage(error, 'Could not improve the reply.')),
  })
}

export function useRewriteReply() {
  const toast = useToast()
  return useMutation({
    mutationFn: (body) => api('/support/rewrite', { method: 'POST', body }),
    onError: (error) => toast.error(errorMessage(error, 'Could not rewrite the draft.')),
  })
}

export function useSaveTicket() {
  const queryClient = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: (body) => api('/support/tickets', { method: 'POST', body }).then((d) => d.ticket),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.supportTickets })
      toast.success('Saved to support KB')
    },
    onError: (error) => toast.error(`Not saved — ${errorMessage(error)}`),
  })
}

export function useDeleteTicket() {
  const queryClient = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: (id) => api(`/support/tickets/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.supportTickets })
      toast.success('Removed from support KB')
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not delete the ticket.')),
  })
}
